import { createFiqlFilter, parseDashboardResponse } from "../../lib/helper";

import Constants from "../../utils/CommonConstants";
import { isOwnerAgentRole } from "../../utils/loginUtils";
import { instance } from "../../lib/api";
import { gaTrackingFn } from "../../tracking/tracking";
import ownerCallStatus from "../../tracking/callStatus";
import constant from "../../utils/CommonConstants";
import ownerConstant from "../../utils/ownerConstants";
import { batch } from "react-redux";
import {
  buildPayPerLeadMetadata,
  filterPricingParametersByLockedLeads,
  formatDynamicPropertiesData,
  getPayPerLeadPackageDetails,
  getProductDataFromFsd,
  makeOwnerResLeadListQuery,
  shouldShowPayPerLead,
  PAY_PER_LEAD_DISPLAY_NAME,
  PRODUCT_NAME_PAY_PER_LEAD,
} from "../../utils/payPerLead";

export const OWNER_DASHBOARD_STATS = "OWNER_DASHBOARD_STATS";
export const GET_OWNER_FIELD_LIST = "GET_OWNER_FIELD_LIST";
export const CALLING_CUSTOMER = "CALLING_CUSTOMER";
export const OWNER_PACKAGES = "OWNER_PACKAGES";
export const OWNER_COMMERCIAL_PACKAGES = "OWNER_COMMERCIAL_PACKAGES";
export const SHARE_LINK = "SHARE_LINK";
export const SEND_PACKAGE_INFO = "SEND_PACKAGE_INFO";
export const CALLING_QUEUE = "CALLING_QUEUE";
export const RESET_CALLING_CUSTOMER = "RESET_CALLING_CUSTOMER";
export const CALLING_QUEUE_COUNT = "CALLING_QUEUE_COUNT";
export const AGENT_STATUS_UPDATE = "AGENT_STATUS_UPDATE";
export const CALLING_QUEUE_CASE = "CALLING_QUEUE_CASE";
export const RESET_OWNER_CALLING_STATUS = "RESET_OWNER_CALLING_STATUS";
export const PAYMENT_DONE_ACTIVATION_COUNT = "PAYMENT_DONE_ACTIVATION_COUNT";
export const PAYMENT_DONE_ACTIVATION_DATA = "PAYMENT_DONE_ACTIVATION_DATA";
export const PACKAGE_HISTORY = "PACKAGE_HISTORY";
export const CHANGE_AGENT_STATUS_API = "CHANGE_AGENT_STATUS_API";
export const AGENT_NUMBER_INFO = "AGENT_NUMBER_INFO";
export const CALL_HANGUP_SUCCESS = "CALL_HANGUP_SUCCESS";
export const CALL_HANGUP_FAIL = "CALL_HANGUP_FAIL";
export const RESET_CALL_HANGUP = "RESET_CALL_HANGUP";
export const ADD_ON_OWNER_PACKAGES = "ADD_ON_OWNER_PACKAGES";
export const PPL_PACKAGE_DATA = "PPL_PACKAGE_DATA";

const addToFIQL = (fiqlFilter, key, value) => {
  const equal = {};
  equal[key] = value;
  fiqlFilter.push({
    equal,
  });
  return fiqlFilter;
};

const getAddOnsOnwer = (packagesObj = {}) => {
  const { results: allPackages = {}, comboMap: { addOn: addOnObject = {} } = {} } =
    packagesObj;
  const addOnPackages = {};

  for (const [type, packages] of Object.entries(allPackages)) {
    const TYPE_NAME = type.toUpperCase();
    if (!addOnPackages[TYPE_NAME]) {
      addOnPackages[TYPE_NAME] = {};
    }

    for (const [packageName, packageDetails] of Object.entries(packages)) {
      const { packageId, actualPrice, markupPrice, discountedPrice, promotedDays } =
        packageDetails || {};

      if (addOnObject[packageId]) {
        addOnPackages[TYPE_NAME][packageName] = {
          packageId,
          actualPrice,
          markupPrice,
          discountedPrice,
          promotedDays,
          name: `${type} - ${packageName}`,
          allowedBasePackages: addOnObject[packageId],
        };
      }
    }
  }

  return addOnPackages;
};

const filterPackageMap = (packageMap, keep) =>
  Object.fromEntries(
    Object.entries(packageMap).map(([service, packages]) => [
      service,
      Object.fromEntries(
        Object.entries(packages || {}).filter(([packageKey]) =>
          keep(service, packageKey)
        )
      ),
    ])
  );

// Housing Assured Agents get only the Housing Assured packages (and add-ons
// that attach to them); every other user gets everything except those.
export const filterOwnerPackagesByRole = (
  { results, commercialResults } = {},
  addOnPackages = {},
  isHousingAssuredAgent = false
) => {
  const { HOUSING_ASSURED_PACKAGE_KEY, HOUSING_ASSURED_SERVICES } = ownerConstant;
  const isHousingAssuredPackage = (service, packageKey) =>
    HOUSING_ASSURED_SERVICES.includes(service) &&
    packageKey === HOUSING_ASSURED_PACKAGE_KEY;
  const housingAssuredPackageIds = HOUSING_ASSURED_SERVICES.map(
    (service) => results?.[service]?.[HOUSING_ASSURED_PACKAGE_KEY]?.packageId
  ).filter((packageId) => packageId !== undefined);

  const isVisibleAddOn = ({ allowedBasePackages = [] }) => {
    const isHousingAssuredBase = (id) => housingAssuredPackageIds.includes(id);
    return isHousingAssuredAgent
      ? allowedBasePackages.some(isHousingAssuredBase)
      : !allowedBasePackages.length ||
          !allowedBasePackages.every(isHousingAssuredBase);
  };
  const isVisiblePackage = (service, packageKey) => {
    const addOn = addOnPackages[service.toUpperCase()]?.[packageKey];
    if (addOn) return isVisibleAddOn(addOn);
    return isHousingAssuredPackage(service, packageKey) === isHousingAssuredAgent;
  };

  return {
    results: results && filterPackageMap(results, isVisiblePackage),
    commercialResults: isHousingAssuredAgent ? {} : commercialResults,
    addOnPackages: filterPackageMap(addOnPackages, (service, packageKey) =>
      isVisibleAddOn(addOnPackages[service][packageKey])
    ),
  };
};

const addDateRangeFilter = (fiqlFilter = [], key, dateRange) => {
  const dates = {};
  dates.from = dateRange.start ? dateRange.start.valueOf() : 0;
  dates.to = dateRange.end ? dateRange.end.valueOf() : new Date().getTime();
  const range = {};
  range[key] = dates;
  fiqlFilter.push({ range });
  return fiqlFilter;
};

const setExtraFIQLFilters = (filters) => {
  let fiqlFilter = [];
  let values;
  if (filters) {
    if (filters.statusValues?.length) {
      values = filters.statusValues.map((status) => {
        return filters.statusMapping[status];
      });
      fiqlFilter = addToFIQL(fiqlFilter, "statusId", values);
    }
    if (filters.orderIds?.length) {
      fiqlFilter = addToFIQL(fiqlFilter, "opportunityId", filters.orderIds);
    }
    if (filters.selectedAgents && Object.keys(filters.selectedAgents).length) {
      values = Object.keys(filters.selectedAgents).map((agent) => {
        return filters.selectedAgents[agent].id;
      });
      fiqlFilter = addToFIQL(fiqlFilter, "agentId", values);
    }
    if (filters.selectedCities && Object.keys(filters.selectedCities).length) {
      values = Object.keys(filters.selectedCities).map((city) => {
        return filters.selectedCities[city];
      });
      fiqlFilter = addToFIQL(fiqlFilter, "cityIds", values);
    }
    if (filters.opportunityName) {
      fiqlFilter = addToFIQL(
        fiqlFilter,
        "opportunityName",
        encodeURIComponent(filters.opportunityName)
      );
    }
    if (filters.accountName) {
      fiqlFilter = addToFIQL(
        fiqlFilter,
        "accountName",
        encodeURIComponent(filters.accountName)
      );
    }
    if (filters.contactNumber) {
      fiqlFilter = addToFIQL(
        fiqlFilter,
        "phoneNumbers",
        encodeURIComponent(filters.contactNumber)
      );
    }
    if (filters.accountIds?.length) {
      fiqlFilter = addToFIQL(fiqlFilter, "accountId", filters.accountIds);
    }
    if (filters.createdAt?.start || filters.createdAt?.end) {
      fiqlFilter = addDateRangeFilter(fiqlFilter, "createdAt", filters.createdAt);
    }
    if (filters.updatedAt?.start || filters.updatedAt?.end) {
      fiqlFilter = addDateRangeFilter(fiqlFilter, "updatedAt", filters.updatedAt);
    }
  }
  return fiqlFilter;
};
const getTeleSalesDashboardInstance = (isPost, { url, postUrl, strSelector }) => {
  if (isPost) {
    return instance({
      url: postUrl,
      method: "POST",
      data: strSelector,
      headers: {
        "Content-Type": "application/text",
      },
    });
  }

  return instance({
    url: url,
    method: "get",
  });
};

export const getTeleSalesDashboard = (
  isPost = false,
  filter,
  agentId = null,
  keys,
  forDashboard = true,
  oldFilters = null,
  isOwnerAgent = true,
  filters
) => {
  let fiqlFilter = setExtraFIQLFilters(filters);
  const selectorArr = [];
  const paging =
    oldFilters && oldFilters.paging ? oldFilters.paging : { start: 0, rows: 0 };
  keys.forEach((key) => {
    const keyFilter = filter[key];
    const filters = {};

    if (keyFilter.addAsFilter) {
      const fiqlFilter = createFiqlFilter(keyFilter, agentId, oldFilters, filters);
      if (fiqlFilter.and.length > 0) {
        filters.filters = fiqlFilter;
      }
    }
    filters.key = keyFilter.key;
    if (agentId && !keyFilter.useDiscountApprovingUserId) {
      filters.agentId = agentId;
    }

    filters.team = true;
    filters.paging = paging;
    if (keyFilter.caseSubTypeId) {
      filters.caseSubTypeId = keyFilter.caseSubTypeId;
    }
    if (keyFilter.sortKey) {
      filters.sort = keyFilter.sortKey;
    }

    selectorArr.push(filters);
  });
  if (
    fiqlFilter.length &&
    selectorArr[0] &&
    selectorArr[0].filters &&
    selectorArr[0].filters.and
  )
    selectorArr[0].filters.and = [...selectorArr[0].filters.and, ...fiqlFilter];
  const strSelector = JSON.stringify(selectorArr);

  const url = `/mystique/v1/soc/key-search?salesOpportunitySelectorsArray=${strSelector}`;
  const postUrl = "/mystique/v1/soc/post/key-search";

  return (dispatch) => {
    return getTeleSalesDashboardInstance(isPost, { url, postUrl, strSelector })
      .then((response) => {
        if (forDashboard) {
          dispatch({
            type: OWNER_DASHBOARD_STATS,
            payload: {
              filters: oldFilters,
              dashboard: parseDashboardResponse(
                response.data.data,
                keys,
                isOwnerAgent
              ),
            },
          });
        } else {
          dispatch({
            type: GET_OWNER_FIELD_LIST,
            data: response.data.data && response.data.data[0],
          });
        }
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

export const getOwnerCallingQueue = (
  start,
  rows,
  countOnly = false,
  queueType,
  statusIds = [],
  filterState = {}
) => {
  let url = `/mystique/v1/soc/owner-calling-queue?start=${start}&rows=${rows}`;
  const {
    selectedAgents,
    createdAt: { start: startDate, end: endDate } = {},
    ownerNumber,
    ownerName,
  } = filterState;
  if (queueType === "linkSent") {
    url = url.concat(
      `&statusIds=${statusIds.join(",")}&ownerOpportunityStageIds=98`
    );
    if (selectedAgents) {
      url = url.concat(`&agentIds=${selectedAgents}`);
    }
    if (startDate && endDate) {
      url = url.concat(`&dateFrom=${startDate}&dateTo=${endDate}`);
    }
    if (ownerNumber) {
      url = url.concat(`&ownerContacts=${ownerNumber}`);
    }
    if (ownerName) {
      url = url.concat(`&ownerNames=${ownerName}`);
    }
  }
  return (dispatch) => {
    return instance({
      url,
      method: "get",
    })
      .then((response) => {
        if (countOnly) {
          dispatch({
            type: CALLING_QUEUE_COUNT,
            queueType,
            payload: response.data.totalCount,
          });
        } else {
          dispatch({
            type: CALLING_QUEUE,
            data: response.data,
          });
        }
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

/**
 * To Implement
 * Api call handling for call integration
 */

export const handleCall = (
  caseId,
  number,
  isBrokerAgent = false,
  isBuilderAgent = false
) => {
  let subType = "HousingOwner";
  if (isBrokerAgent) {
    subType = "Housing";
  } else if (isBuilderAgent) {
    subType = "HousingBuilder";
  }
  const url = `/mystique/v1/cases/${caseId}/call?type=SOCASE&custNumber=${number}&subType=${subType}`;
  return (dispatch) => {
    return instance({
      url,
      method: "get",
    })
      .then(
        ({
          data: {
            data: { status, id, message },
          },
        }) => {
          dispatch({
            type: CALLING_CUSTOMER,
            payload: {
              status,
              id,
              message: message ? `${status} - ${message}` : null,
            },
          });
          if (!isBrokerAgent) {
            const { CALL_TRIGGERED, CALL_TRIGGERED_FAILURE } = ownerCallStatus;
            dispatch(
              gaTrackingFn(id ? CALL_TRIGGERED : CALL_TRIGGERED_FAILURE, {
                communication_id: id,
              })
            );
          }

          return id;
        }
      )
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
        dispatch({
          type: RESET_CALLING_CUSTOMER,
        });
        !isBrokerAgent &&
          dispatch(gaTrackingFn(ownerCallStatus.CALL_TRIGGERED_FAILURE, {}));

        return err;
      });
  };
};

export const handleEndCall = (agentPhoneNumber) => {
  const HANGUP_CALL_URL = "kira/bspl/hangup";
  const queryParams = new URLSearchParams({
    agentPhone: agentPhoneNumber,
  });
  const url = `${HANGUP_CALL_URL}?${queryParams.toString()}`;

  return (dispatch) => {
    return instance({
      url,
      method: "get",
    })
      .then((response) => {
        const { message } = response.data;
        dispatch({
          type: CALL_HANGUP_SUCCESS,
          errMsg: message,
        });
      })
      .catch((err) => {
        dispatch({
          type: CALL_HANGUP_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

const paymentDoneActivationDetails = (
  userType = Constants.housingOwnerAgentRole,
  start = 0,
  rows = 0,
  team = true,
  userId
) => {
  let clientType =
    userType == constant.builderTelesalesAgentRole ? "BUILDER" : "BROKER";
  let url = `/sapna/v1/opportunity/get-owner-product-activation?clientType=${clientType}`;

  if (isOwnerAgentRole(userType)) {
    url = "/sapna/v1/opportunity/get-owner-product-activation";
  }
  const params = { userId };
  let productActivated = true;
  let data = [
    {
      team,
      paging: {
        start,
        rows,
      },
      productActivated,
    },
  ];
  productActivated = false;
  data.push({
    team,
    paging: {
      start,
      rows,
    },
    productActivated,
  });
  return instance({
    url,
    method: "post",
    data: data,
    params,
  });
};

export const populatePaymentDoneData = (
  userInfoRole,
  start,
  rows,
  team,
  productActivated,
  userId
) => {
  return (dispatch) => {
    paymentDoneActivationDetails(userInfoRole, start, rows, team, userId)
      .then(({ data: { data = [] } = {} }) => {
        dispatch({
          type: PAYMENT_DONE_ACTIVATION_DATA,
          payload: (productActivated ? data[0] : data[1]) || {},
        });
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

export const getPaymentAndActivationData = (userType) => {
  return (dispatch) => {
    const keys = ["activationComplete", "activationPending"];

    paymentDoneActivationDetails(userType)
      .then((response) => {
        const { data } = response.data;
        for (let index = 0; index < 2; index++) {
          let { totalCount } = data[index];
          keys?.[index] &&
            dispatch({
              type: PAYMENT_DONE_ACTIVATION_COUNT,
              payload: {
                key: keys[index],
                totalCount,
              },
            });
        }
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};
export const getOwnerPackages = ({ profileUUID = "" }) => {
  const url = `/sapna/v5/package-price/owner?accountAgentType=${constant.accountAgentType}&profileUuid=${profileUUID}`;
  return (dispatch, getState) => {
    return instance({
      url,
      method: "get",
    })
      .then((response) => {
        const { userInfo } = getState();
        const isHousingAssuredAgent = (userInfo?.roles || []).includes(
          constant.housingAssuredAgentRole
        );
        const apiData = response?.data?.data || {};
        const { results, commercialResults, addOnPackages } =
          filterOwnerPackagesByRole(
            apiData,
            getAddOnsOnwer(apiData),
            isHousingAssuredAgent
          );
        batch(() => {
          dispatch({
            type: OWNER_PACKAGES,
            payload: results,
          });
          dispatch({
            type: OWNER_COMMERCIAL_PACKAGES,
            payload: commercialResults,
          });
        });

        dispatch({
          type: ADD_ON_OWNER_PACKAGES,
          payload: addOnPackages,
        });
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

const dispatchPplPackageData = (dispatch, payload) => {
  dispatch({
    type: PPL_PACKAGE_DATA,
    payload,
  });
};

/**
 * Client-side aggregation of Pay Per Lead.
 * Reuses ownerPackageMap and liveAndPendingListings from Redux (v5 + helper/36) instead of v4 + Casa.
 */
export const getPayPerLeadData = ({ profileUUID = "" } = {}) => {
  return (dispatch, getState) => {
    const clearPpl = () => dispatchPplPackageData(dispatch, null);
    const { callingQueueOpportunity = {} } = getState();
    const showPayPerLead = shouldShowPayPerLead(callingQueueOpportunity);

    if (!profileUUID || !String(profileUUID).trim() || !showPayPerLead) {
      clearPpl();
      return Promise.resolve(null);
    }

    const v7Url = `/sapna/v7/package-price/owner?accountAgentType=${constant.accountAgentType}&profileUuid=${profileUUID}`;

    return instance({ url: v7Url, method: "get" })
      .then((v7Res) => {
        const pricingData = v7Res?.data?.data ?? v7Res?.data ?? {};
        const { ownerPackageMap = {}, liveAndPendingListings = {} } = getState();
        const results = ownerPackageMap;
        const taxDto = {};

        if (
          !pricingData ||
          typeof pricingData !== "object" ||
          Object.keys(pricingData).length === 0 ||
          !results ||
          typeof results !== "object" ||
          Object.keys(results).length === 0
        ) {
          clearPpl();
          return null;
        }

        const pkgResult = getPayPerLeadPackageDetails({
          pricingData,
          packageName: PAY_PER_LEAD_DISPLAY_NAME,
          results,
        });

        const {
          packageData: payPerLeadData,
          serviceType,
          service,
          listingIds = [],
        } = pkgResult || {};

        if (!payPerLeadData) {
          clearPpl();
          return null;
        }

        const listingId = listingIds[0];
        if (!listingId) {
          clearPpl();
          return null;
        }

        const allListings = [
          ...(liveAndPendingListings.BUY || []),
          ...(liveAndPendingListings.RENT || []),
          ...(liveAndPendingListings.PG || []),
        ];
        const eligibleListing =
          allListings.find(({ id }) => String(id) === String(listingId)) || {};

        const leadsApiPayload = {
          id: listingId,
          service: serviceType,
          page: 1,
          page_size: 500,
          isPaid: false,
          filter: "all",
        };

        const leadsQuery = {
          ...makeOwnerResLeadListQuery(leadsApiPayload),
          get_hits: false,
        };

        const leadsQueryString = new URLSearchParams(
          Object.entries({
            ...leadsQuery,
            profile_uuid: profileUUID,
          }).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== null) {
              acc[k] = v;
            }
            return acc;
          }, {})
        ).toString();

        const helperId = process.env.BUILD_ENV === "prod" ? 37 : 39;
        const leadsUrl = `/sapna/v1/housing/helper/${helperId}?${leadsQueryString}`;
        const staticUrl = `/sapna/dynamicProperties/get-by-keys?keys=pay_per_lead`;

        return Promise.all([
          instance({ url: leadsUrl, method: "get" }).catch(() => ({ data: {} })),
          instance({ url: staticUrl, method: "get" }).catch(() => ({ data: {} })),
        ]).then(([leadsResponse, staticResponse]) => {
          const leadInner =
            leadsResponse?.data?.data?.data || leadsResponse?.data?.data || {};
          const {
            blocked_lead_count: blockedLeadCount = 0,
            total_lead_count: totalLeadCount = 0,
            lead_cap: leadCap = 0,
          } = leadInner;

          const lockedLeadCount = Math.max(
            0,
            Math.min(blockedLeadCount, totalLeadCount - leadCap)
          );

          const productDetails =
            getProductDataFromFsd({
              property: eligibleListing,
              productName: PRODUCT_NAME_PAY_PER_LEAD,
            }) || {};

          const {
            initial_credits: initialCredits = 0,
            credits_consumed: creditsConsumed = 0,
          } = productDetails;

          const unusedEnquiries = Math.max(0, initialCredits - creditsConsumed);

          const filteredPricingParameters = filterPricingParametersByLockedLeads(
            payPerLeadData.pricing_parameters || [],
            Math.max(0, lockedLeadCount - unusedEnquiries)
          );

          const rawStatic = staticResponse?.data?.data ?? staticResponse?.data ?? [];
          const staticArray = Array.isArray(rawStatic) ? rawStatic : [];
          const formattedStatic = formatDynamicPropertiesData(staticArray) || {};
          const { pay_per_lead: formattedPayPerLeadStaticData = {} } =
            formattedStatic || {};

          const { upsellPackage = null } = pkgResult;

          const metadataBase = buildPayPerLeadMetadata({
            pricingParameters: filteredPricingParameters,
            lockedLeadCount,
            productDetails,
            upsellPackage,
            serviceType,
            taxDto,
            payPerLeadStaticData: formattedPayPerLeadStaticData || {},
            showPayPerLead,
          });

          const metadata = {
            ...metadataBase,
            totalLeadCount,
            unpurchasedLockedLeads: Math.max(0, lockedLeadCount - unusedEnquiries),
            eligibleListingIds: listingIds,
          };

          const responsePayload = {
            ...payPerLeadData,
            service,
            pricing_parameters: filteredPricingParameters,
            metadata,
          };

          dispatchPplPackageData(dispatch, responsePayload);
          return responsePayload;
        });
      })
      .catch(() => {
        clearPpl();
        return null;
      });
  };
};

export const shareLink = (data) => {
  const url = "/sapna/v4/opportunity";
  return (dispatch) => {
    return instance({
      url,
      method: "post",
      data,
    })
      .then((response) => {
        dispatch({
          type: SHARE_LINK,
          payload: response.data.data,
          snackbar: response.data.data
            ? "Link shared successfully"
            : "Link sharing failed",
        });
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

export const sendOwnerPackageInfo = (data) => {
  const url = "/sapna/v2/packages/share-details";
  return (dispatch) => {
    return instance({
      url,
      method: "post",
      data,
    })
      .then((response) => {
        dispatch({
          type: SEND_PACKAGE_INFO,
          payload: response.data.data
            ? "Package Details shared successfully"
            : "Package Details sharing failed",
        });
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      });
  };
};

export const changeAgentStatus = (newStatus) => {
  const url = `/sapphire/v1/agents/status?status=${newStatus}`;
  return (dispatch) => {
    dispatch({
      type: CHANGE_AGENT_STATUS_API,
      payload: true,
    });
    return instance({
      url,
      method: "put",
    })
      .then(() => {
        dispatch({
          type: AGENT_STATUS_UPDATE,
          payload: newStatus,
        });
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
      })
      .finally(() => {
        dispatch({
          type: CHANGE_AGENT_STATUS_API,
          payload: false,
        });
      });
  };
};

export const pullCase = (userInfoRole = Constants.housingOwnerAgentRole) => {
  let url = "/mystique/v1/soc/pull";
  if (userInfoRole == Constants.brokerTelesalesAgentRole) {
    url += "/broker";
  } else if (userInfoRole == Constants.builderTelesalesAgentRole) {
    url += "/builder";
  }
  return (dispatch) => {
    return instance({
      url,
      method: "get",
    })
      .then((response) => {
        const startOfTimer = new Date().getTime();
        const pullCaseData = response?.data?.data || {};
        dispatch({
          type: CALLING_QUEUE_CASE,
          payload: {
            pullCaseData: {
              ...pullCaseData,
              // showPayPerLead: shouldShowPayPerLead(pullCaseData), // TODO: use this once logic gets fixed on backend
              showPayPerLead: false,
            },
            startOfTimer,
          },
        });
        dispatch({
          type: RESET_CALLING_CUSTOMER,
        });
        dispatch({
          type: RESET_CALL_HANGUP,
        });
        if (!pullCaseData?.id) {
          dispatch({
            type: Constants.SNACKBAR_NOTIFICATION,
            snackBarType: "error",
            msg: "No opportunity available to pull",
          });
        }

        return response;
      })
      .catch((err) => {
        dispatch({
          type: Constants.API_FAIL,
          errMsg: Constants.getErrorMessage(err),
        });
        return err;
      });
  };
};

export const getAgentNumberInfo = (agentId, isBrokerTelesalesAgent) => {
  const AGENT_INFO_URL = "mystique/v1/agentsphone";
  const filters = `agentId==${agentId};sourceId==${
    isBrokerTelesalesAgent ? 31 : 18
  }`;
  const url = `${AGENT_INFO_URL}?filters=${filters}`;
  return (dispatch) => {
    return instance({
      url,
      method: "get",
    })
      .then((response) => {
        const { data: { data = [] } = [] } = response || {};
        dispatch({
          type: AGENT_NUMBER_INFO,
          payload: data,
        });
        return data;
      })
      .catch((err) => {
        dispatch({
          type: constant.API_FAIL,
          errMsg: constant.getErrorMessage(err),
        });
      });
  };
};

export const reloadCase = (caseId, isPrioritisedPull = 1) => {
  const url = `/mystique/v1/soc/${caseId}`;
  return instance({
    url,
    data: { isPrioritisedPull },
    method: "put",
  });
};
export const clientInactiveBeforeCall = (caseId, readTimeData = {}) => {
  const url = `/mystique/v1/soc/${caseId}`;
  return instance({
    url,
    data: readTimeData,
    method: "put",
  });
};

export const getAgentStatus = () => {
  const url = `/sapphire/v1/agents/status`;
  return instance({
    url,
    method: "get",
  })
    .then((response) => {
      return response.data.data.status;
    })
    .catch(() => {});
};
export const getPullStatus = (caseId) => {
  const url = `/mystique/v1/soc/${caseId}`;
  return instance({
    url,
    method: "get",
  })
    .then((response) => {
      return response.data.data.isPrioritisedPull;
    })
    .catch(() => {});
};

export const refreshSocLookup = (opportunityId) => {
  const url = `/mystique/v1/soc/lookup/refresh?opportunityId=${opportunityId}`;
  return instance({
    url,
    method: "get",
  }).catch(() => {});
};

export const resetCallingStatus = () => {
  return (dispatch) => {
    dispatch({
      type: RESET_OWNER_CALLING_STATUS,
    });
  };
};

export const getAgents = () => {
  return (dispatch, getState) => {
    const state = getState();
    const {
      userInfo: { id },
    } = state;
    const url = `/madrox/app/v1/entity/child-list?userId=${id}&status=active&fromCache=false&selector={"fields":["name", "userId"]}`;
    return instance({
      url,
      method: "get",
    }).then((response) => {
      const { data: { data = [] } = {} } = response;
      const agents = data.reduce((acc, agent) => {
        return {
          ...acc,
          [agent.name]: agent,
        };
      }, {});
      dispatch({
        type: "AGENT_LIST",
        agents,
      });
    });
  };
};
const getReadableDate = (timestamp) => {
  return (timestamp && Constants.epochToDate(timestamp * 1000)) || "";
};

const parsePackageHistory = (data) => {
  const products = ["upcoming", "live", "expired"];
  let details = [];
  products.forEach((status) => {
    if (data && data[status]) {
      details = [
        ...details,
        ...data[status].reduce(
          (
            details,
            {
              package_name: name,
              products_name: products = [],
              start_datetime: startDate,
              end_datetime: endDate,
              slot_id: slotId,
              buy_leads_delivered: buyLeads,
              rent_leads_delivered: rentLeads,
              commercial_buy_leads_delivered: commercialBuyLeads = 0,
              commercial_rent_leads_delivered: commercialRentLeads = 0,
            }
          ) => [
            ...details,
            ...(products.length
              ? products.map((name) => ({
                  name,
                  status,
                  startDate: getReadableDate(startDate),
                  endDate: getReadableDate(endDate),
                  slotId,
                  buyLeads,
                  rentLeads,
                  commercialBuyLeads,
                  commercialRentLeads,
                }))
              : [
                  {
                    name,
                    status,
                    startDate: getReadableDate(startDate),
                    endDate: getReadableDate(endDate),
                    slotId,
                    buyLeads,
                    rentLeads,
                    commercialBuyLeads,
                    commercialRentLeads,
                  },
                ]),
          ],
          []
        ),
      ];
    }
  });
  return details;
};
export const getPackageHistory = (accountId) => {
  return (dispatch) => {
    const url = `/sapna/v1/housing/helper/20?profile_uuid=${accountId}`;
    return instance({
      url,
      method: "get",
    }).then((response) => {
      const { data: { data = {} } = {} } = response;
      dispatch({
        type: PACKAGE_HISTORY,
        payload: {
          packagesHistory: parsePackageHistory(data),
        },
      });
    });
  };
};
